/// ----------------------------------------------------------------------------------------
///Copyright (C) 2016 to the present, Crestron Electronics, Inc.
///All rights reserved.
///No part of this software may be reproduced in any form, machine
///or natural, without the express written consent of Crestron Electronics.
/// ----------------------------------------------------------------------------------------
/// <file>
/// <name>routing.service.ts</name>
///
/// <description>
///   Listens to broadcasts from components and makes appropriate routing.
///   Need for decoupling components from Hydrogen application
/// </description>
///
/// <author>Yaron Habot</author>
/// <created>21 Nov 2016</created>
///
/// <changelog>
///   Date  Who Version     Flag Comments
/// ======= === =========== ==== ===========================================================
/// 21Nov16 YH  1.0.0000         First cut
/// 01Dec16 YH                   Task 9542:Revamp Hydrogen Portal left hand side vertical
/// 12Dec16 AA                   Added subscription to AccountCreateSelected
/// 17Apr17 YH                   Bug 123784 - [Users]On creation of a new user, the user does not receive an email to reset the password
/// </changelog>
///
/// </file>
/// ----------------------------------------------------------------------------------------
import { Injectable } from '@angular/core';
import { Router,ActivatedRoute } from '@angular/router';
import { Subscription } from "rxjs/Subscription";

import {MediatorService} from './mediator.service';
import {MediatorEventLookup} from './mediator-event-lookup.service';
import { HydUser } from '../models/user';

@Injectable()
export class HydRoutingService {

    constructor(
        private _router: Router,
        private _mediator: MediatorService,
        private _hydUser : HydUser,
        private route:ActivatedRoute
    )
    {
        this.subscribeToGroupSelected();

        this.subscribeToGroupDeleted();

        this.subscribeToDeviceSelected();

        this.subscribeToAccountCreateSelected();
        this.subscribeToAccountSelected();

        this.subscribeToUserSelected();

        this.subscribeToCreateNewUser();    //YH 12/27/2016 Task 9829:Hydrogen Portal - User Management: Add/Remove User from Customer UI

        this.subscribeToInviteNewUser();    //YH 12/27/2016 Task 9829:Hydrogen Portal - User Management: Add/Remove User from Customer UI

        this.subscribeToEndUserDeleted();    //YH 5/5/2017 Bug 128885 - After deleting a user, user details don't disappear from detail pane

        this.subscribeToPolicySelected();

        this.subscribeToUserAuthenticated();

        this.subscribeToPortalManagementCategorySelected();

        this.subscribeToSettingCategorySelected();
    }

    //-----------------------------------------------
    // subscribeToPortalManagementCategorySelected
    //-----------------------------------------------
    private subscribeToPortalManagementCategorySelected() {
        let inst = this;
        inst._mediator.Event(MediatorEventLookup.PortalManagementCategorySelected).subscribe(
            (payload) => {
                inst._router.navigate([inst.BaseRoute, { outlets: { 'list': ['portalManagement'], 'details': ['portalManagement', payload.value, payload.label.toLowerCase()] } }], { replaceUrl: true });
            },
            err => console.error(err),
            () => { }
        );
    }

    //-----------------------------------------------
    // subscribeToSettingCategorySelected
    //-----------------------------------------------
    private subscribeToSettingCategorySelected() {
        let inst = this;
        inst._mediator.Event(MediatorEventLookup.SettingCategorySelected).subscribe(
            (payload) => {
                inst._router.navigate([inst.BaseRoute, { outlets: { 'list': ['settings'], 'details': ['settings', payload.value, payload.label.toLowerCase()] } }], { replaceUrl: true });
            },
            err => console.error(err),
            () => { }
        );
    }

    //-----------------------------------------------
    // subscribeToAccountSelected
    //-----------------------------------------------
    private subscribeToAccountSelected() {
        let inst = this;
        inst._mediator.Event(MediatorEventLookup.AccountSelected).subscribe(
            (payload) => {
                let route: string[] = payload.split("/");
                inst._router.navigate([inst.BaseRoute, { outlets: { 'list': ['accounts'], 'details': route } }], { replaceUrl: true });
            },
            err => console.error(err),
            () => { }
        );
    }

    //-----------------------------------------------
    // subscribeToAccountCreateSelected
    //-----------------------------------------------
    private subscribeToAccountCreateSelected() {
        let inst = this;
        inst._mediator.Event(MediatorEventLookup.AccountCreateSelected).subscribe(
            (payload) => {
                let route: string[] = payload.split("/");
                inst._router.navigate([inst.BaseRoute, { outlets: { 'list': ['accounts'], 'details': route } }], { replaceUrl: true });
            },
            err => console.error(err),
            () => { }
        );
    }
    //-----------------------------------------------
    // subscribeToGroupSelected
    //-----------------------------------------------
    private subscribeToGroupSelected() {
        let inst = this;
        inst._mediator.Event(MediatorEventLookup.GroupSelected).subscribe(
            (payload) => {
                let lastTab : string = this.GetLastDetailTab("details","groups","status");
                let id =payload.id;
                inst._router.navigate([inst.BaseRoute, { outlets: { 'list': ['groups'], 'details': ['groups',id, lastTab] } }], { replaceUrl: true });
            },
            err => console.error(err),
            () => { }
        );
    }

    private subscribeToGroupDeleted() {
        let inst = this;
        inst._mediator.Event(MediatorEventLookup.GroupDeleted).subscribe(
            data => {
                inst._router.navigateByUrl(inst.BaseRoute +"/(list:groups)");
            },
            err => console.error(err),
            () => { });
    }

    //-----------------------------------------------
    // subscribeToDeviceSelected
    //-----------------------------------------------
    private subscribeToDeviceSelected() {
        let inst = this;
        inst._mediator.Event(MediatorEventLookup.DeviceSelected).subscribe(
            (payload) => {
                let id = payload.id;
                //YH 4/27/2017 Bug 128092 - Clicking on devices did not open appropriate page on the right frame post unassociating and adding devices to a group
                if (id) {
                    let lastTab : string = this.GetLastDetailTab("details","device","status");
                    inst._router.navigate([inst.BaseRoute,   { outlets: { 'list': ['groups'], 'details': ['device', id, lastTab] } }],{ replaceUrl: true });
                }
            },
            err => console.error(err),
            () => { }
        );
    }



    //-----------------------------------------------
    // subscribeToUserSelected
    //-----------------------------------------------
    private subscribeToUserSelected() {
        let inst = this;
        inst._mediator.Event(MediatorEventLookup.UserSelected).subscribe(
            (payload) => {
                let id = payload;
                //inst._router.navigate([inst.BaseRoute, {outlets: {'details':[id]}}]);
                inst._router.navigate([inst.BaseRoute, {outlets: {'list':['users'],'details':['user',id,'profile']}}],{ replaceUrl: true });
            },
            err => console.error(err),
            () => { }
        );
    }

    //YH 12/27/2016 Task 9829:Hydrogen Portal - User Management: Add/Remove User from Customer UI
    //-----------------------------------------------
    // subscribeToCreateNewUser
    //-----------------------------------------------
    private subscribeToCreateNewUser() {
        let inst = this;
        inst._mediator.Event(MediatorEventLookup.UserCreate).subscribe(
            (payload) => {
                inst._router.navigate([inst.BaseRoute, {outlets: {'list':['users'],'details':['user','create','profile']}}],{ replaceUrl: true });
            },
            err => console.error(err),
            () => { }
        );
    }

    //YH 04/17/2017 Bug 123784 - [Users]On creation of a new user, the user does not receive an email to reset the password
    //-----------------------------------------------
    // subscribeToInviteNewUser
    //-----------------------------------------------
    private subscribeToInviteNewUser() {
        let inst = this;
        inst._mediator.Event(MediatorEventLookup.UserInvite).subscribe(
            (payload) => {
                inst._router.navigate([inst.BaseRoute, {outlets: {'list':['users'],'details':['user','invite','profile']}}],{ replaceUrl: true });
            },
            err => console.error(err),
            () => { }
        );
    }

    //-----------------------------------------------
    // subscribeToEndUserDeleted
    //-----------------------------------------------
    private subscribeToEndUserDeleted() {
        let inst = this;
        inst._mediator.Event(MediatorEventLookup.UserDeleted).subscribe(
            (payload) => {
                inst._router.navigateByUrl(inst.BaseRoute +"/(list:users)");
            },
            err => console.error(err),
            () => { }
        );
    }

    //-----------------------------------------------
    // subscribeToPolicySelected
    //-----------------------------------------------
    private subscribeToPolicySelected() {
        let inst = this;
        inst._mediator.Event(MediatorEventLookup.PolicySelected).subscribe(
            (payload) => {
                let id = payload;
                inst._router.navigate([inst.BaseRoute, {outlets: {'list':['policies'],'details':['policy',id]}}],{ replaceUrl: true });
            },
            err => console.error(err),
            () => { }
        );
    }

    //-----------------------------------------------
    // subscribeToUserAuthenticated
    //-----------------------------------------------
    private subscribeToUserAuthenticated() {
        let inst = this;
        inst._hydUser.AuthorizationObservable.subscribe(
            data => {

              let custID = 'CRESTRONIT';
              let destination = "";
              if (this.IsCrestronUser) { 
                destination = 'crestron';
              }
              else {
                destination = 'customer';
              }
              inst._router.navigate([inst.BaseRoute, { outlets: { 'details': [destination, custID] } }], { replaceUrl: true });

            },
            err => console.error(err),
            () => { });
    }

    //-----------------------------------------------
    // subscribeToPolicySelected
    //-----------------------------------------------
    private subscribeToFileLibrarySelected() {
        let inst = this;
        inst._mediator.Event(MediatorEventLookup.FileLibrarySelected).subscribe(
            (payload) => {
                let id = payload;
                inst._router.navigate([inst.BaseRoute, { outlets: { 'list': ['filelist'], 'details': ['filemanage', id] } }], { replaceUrl: true });
            },
            err => console.error(err),
            () => { }
        );
    }



    private OnUserAuthenticated() {

    }

    private get IsCrestronUser() : boolean {
        let ret : boolean = false;
        if (this._hydUser && this._hydUser.Roles)
        {
            let isCrestronUser = this._hydUser.Roles.find(role=>{return role.indexOf("Crestron")!=-1;});
            if (isCrestronUser)
                ret = true;
        }
        return ret;
    }

    private get BaseRoute() : string {
        let baseRoute : string = "/customer";
        if (this.IsCrestronUser)
            baseRoute = "/crestron";
        return baseRoute;
    }

    private GetLastDetailTab(outletName:string,firstPath:string, defaultPath: string) : string {
        let lastTab: string = defaultPath;
        try {
            let detailsRoute : ActivatedRoute;
            detailsRoute = this.route.firstChild.children.find(child=>child.outlet == outletName);
            if (detailsRoute.snapshot.url[0].path == firstPath)
                lastTab = detailsRoute.firstChild.firstChild.snapshot.url[0].path

        }
        catch(err){}

        return lastTab;
    }
}



// WEBPACK FOOTER //
// C:/WS/Fusion/FusionDashboard/src/app/services/routing.service.ts